<template>
  <div>
    <a-card class="customer-header" size="small" :loading="loading">
      <div>
        <a-page-header :title="faultData.title" :sub-title="`故障详情 # RCA-${faultId}`" @back="() => $router.go(-1)">
          <template slot="extra">
            <a-button type="primary" icon="edit" @click="onEdit()"> 编辑 </a-button>
          </template>
          <a-descriptions size="small">
            <a-descriptions-item label="故障等级">
              <a-tag v-if="faultData.level !== null" :color="faultLevels[faultData.level].color">{{ faultLevels[faultData.level].label }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="关联业务域">
              <a-tag v-for="(item, index) in faultData.domains" :key="index" color="pink">{{ item.name }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="关联项目">
              <a-tag v-for="(item, index) in faultData.projects" :key="index" color="pink">{{ item.name }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="上报时间"> {{ faultData.reported_time || '--' }} </a-descriptions-item>
            <a-descriptions-item label="开始时间"> {{ faultData.started_time || '--' }} </a-descriptions-item>
            <a-descriptions-item label="解决时间"> {{ faultData.resolved_time || '--' }} </a-descriptions-item>
            <a-descriptions-item>
              <template slot="label">
                影响时长
                <a-popover title="影响时长">
                  <template slot="content">
                    <span>从【开始时间】到【解决时间】所经历的时长</span>
                  </template>
                  <a-icon type="question-circle" />
                </a-popover>
              </template>
              {{ faultData.impact_duration || '--' }}
            </a-descriptions-item>
            <a-descriptions-item>
              <template slot="label">
                处理时长
                <a-popover title="处理时长">
                  <template slot="content">
                    <span>从【上报时间】到【解决时间】所经历的时长</span>
                  </template>
                  <a-icon type="question-circle" />
                </a-popover>
              </template>
              {{ faultData.handle_duration || '--' }}
            </a-descriptions-item>
            <a-descriptions-item label="故障标签">
              <a-tag v-for="(tag, index) in faultData.tags" :key="index" :color="tag.color">{{ tag.name }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="创建人员"> {{ faultData.creator ? faultData.creator.nickname : '--' }} </a-descriptions-item>
            <a-descriptions-item label="创建时间"> {{ faultData.created_time }} </a-descriptions-item>
            <a-descriptions-item label="更新时间"> {{ faultData.updated_time }} </a-descriptions-item>
          </a-descriptions>
        </a-page-header>
        <a-tabs v-model="activeKey" size="small">
          <a-tab-pane v-if="documentToken" key="1" tab="故障复盘">
            <fei-shu-document-new
              :token="documentToken"
              :options="{
                height: '70vh',
                everyoneEdit: true // 所有人可编辑
              }"
            />
          </a-tab-pane>
          <a-tab-pane v-else key="1" tab="问题详情">
            <a-collapse v-model="activeKeys" :bordered="false">
              <template #expandIcon="props">
                <a-icon type="caret-right" :rotate="props.isActive ? 90 : 0" />
              </template>
              <a-collapse-panel key="1" class="fault-panel">
                <span slot="header">故障描述</span>
                <vditor-preview :content="faultData.content" />
              </a-collapse-panel>
              <a-collapse-panel key="2" class="fault-panel">
                <span slot="header">故障归因</span>
                <vditor-preview :content="faultData.attribution" />
              </a-collapse-panel>
              <a-collapse-panel key="3" class="fault-panel">
                <span slot="header"> 评论 <a-button type="link" class="collapse-button" icon="plus" @click.stop="onAddComment"> 添加 </a-button> </span>
                <problem-comment ref="comment" :source-id="faultId" source-type="problem" />
              </a-collapse-panel>
            </a-collapse>
          </a-tab-pane>
          <a-tab-pane key="2" force-render>
            <a-badge slot="tab" :count="faultData.duty.length"><span class="head-example">责任划分</span></a-badge>
            <fault-duty :source-data="faultData.duty" @callback="getFaultDetail" />
          </a-tab-pane>
          <a-tab-pane key="3" force-render>
            <a-badge slot="tab" :count="faultData.tasks.length"><span class="head-example">跟进任务</span></a-badge>
            <a-button type="primary" icon="plus" class="add-task-bt" @click="onAddTask"> 添加任务 </a-button>
            <fault-task-data-list ref="faultTaskRef" :table-data="faultData.tasks" :pagination="false" :loading="loading" @change="getFaultDetail" />
          </a-tab-pane>
          <a-tab-pane key="4" tab="变更日志" force-render>
            <problem-record ref="record" :source-id="faultId" />
          </a-tab-pane>
        </a-tabs>
      </div>
    </a-card>
    <fault-drawer :display="editVisible" :source-id="faultId" @close="close" />
  </div>
</template>
<script>
import { faultDetail } from '@/api/event/fault';
import { faultLevels } from '@/utils/dictionary';
import { queryDocument } from '@/api/business/document';

import FaultTaskDataList from '@/pages/event/components/FaultTaskDataList';
import FaultDuty from '@/pages/event/components/FaultDuty';
import VditorPreview from '@/components/VditorEditor/preview';
import FaultDrawer from '@/pages/event/components/FaultDrawer';
import FeiShuDocumentNew from '@/components/FeiShu/DocumentNew';
import ProblemRecord from '@/pages/event/components/ProblemRecord';
import ProblemComment from '@/pages/event/components/ProblemComment';

export default {
  components: { VditorPreview, FaultTaskDataList, FaultDrawer, FaultDuty, ProblemRecord, ProblemComment, FeiShuDocumentNew },
  data() {
    return {
      loading: true,
      editVisible: false,
      faultLevels,
      faultData: {
        level: null,
        projects: [],
        tags: [],
        duty: [],
        tasks: []
      },
      documentToken: null,
      activeKey: '1',
      activeKeys: ['1', '2', '3'],
      faultId: parseInt(this.$route.query.faultId) || null
    };
  },
  computed: {
    project() {
      return projects => {
        return projects
          .map(p => {
            return p.name;
          })
          .join('; ');
      };
    }
  },
  created() {
    this.getFaultDetail();
    this.getDocuments();
  },

  methods: {
    /**
     * 获取故障详情
     */
    getFaultDetail() {
      faultDetail({ id: this.faultId })
        .then(res => {
          this.faultData = res.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    /**
     * 添加评论
     */
    onAddComment() {
      this.$nextTick(() => {
        if ('3'.includes(this.activeKeys) === false) {
          this.activeKeys.push('3');
        }
        this.$refs['comment'].onAddComment();
      });
    },

    /**
     * 添加任务
     */
    onAddTask() {
      this.$nextTick(() => {
        this.$refs.faultTaskRef.onAdd(this.faultId);
      });
    },

    /**
     * 编辑故障
     */
    onEdit() {
      this.editVisible = true;
    },

    /**
     * 查询需求关联的文档
     */
    getDocuments() {
      queryDocument({
        objectId: this.faultId,
        objectType: 'fault',
        pageNumber: 1,
        pageSize: 10,
        category: 'RCA'
      }).then(res => {
        this.documentToken = res.data.items.find(item => item.category === 'RCA')?.token;
      });
    },

    /**
     * 关闭弹窗
     */
    close(type) {
      this.editVisible = false;
      if (type === 'submit') {
        this.getFaultDetail();
        this.$nextTick(() => {
          this.$refs['comment'].getCommentList();
          this.$refs['record'].getProblemRecord();
        });
      }
    }
  }
};
</script>

<style lang="less" scoped>
:deep(.customer-header) {
  .ant-card-body {
    padding: 16px 16px 16 16px;
  }
  .ant-page-header {
    padding: 0;
  }
  .ant-descriptions-vertical {
    .ant-descriptions-row > th,
    .ant-descriptions-row > td {
      padding-bottom: 8px;
    }
  }
}

:deep(.ant-page-header-footer) {
  margin-top: 0px;
  .ant-tabs-bar .ant-tabs-nav .ant-tabs-tab {
    font-size: 14px;
    font-weight: bold;
  }
}

:deep(.info) {
  .ant-descriptions-title {
    margin-bottom: 8px;
  }
  .ant-descriptions-item {
    padding-left: 8px;
    span {
      float: left;
    }
  }
}
:deep(.task) {
  .ant-descriptions-title {
    margin-bottom: 8px;
  }
}

:deep(.fault-panel) {
  border: 0;
  overflow: hidden;
  border-radius: 4px;
  background: #fff;
  .ant-collapse-header {
    background: #fafafa;
    font-weight: bold;
  }
  .ant-collapse-content-box {
    padding: 0px 32px 8px 32px;
  }
}
.head-example {
  display: inline-block;
  padding-right: 8px;
  // font-size: 16px;
}
:deep(.ant-badge-count) {
  height: 16px;
  min-width: 16px;
  line-height: 16px;
  font-size: 8px;
  padding: 0 3px;
  background-color: #1890ff;
}

.collapse-button {
  color: #86909c;
}

.add-task-bt {
  margin-bottom: 8px;
}
</style>
